import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Globe2, ShieldCheck, Zap, MapPin,
  Network, Radar, Lock, Layers, Terminal, ArrowRight,
} from "lucide-react";
import TransactionDemo from "@/components/TransactionDemo";
import { Eyebrow, SectionHeader, Reveal } from "@/components/SectionHeader";

const TRUST = [
  { icon: Globe2,     label: "Multi-network connectivity",   sub: "Not locked to one rail" },
  { icon: ShieldCheck,label: "Bank-grade security",           sub: "On every message" },
  { icon: Zap,        label: "Real-time status, always",      sub: "Normalized end-to-end" },
  { icon: MapPin,     label: "Headquartered in Singapore",    sub: "Built for global scale" },
];

const WHY = [
  {
    icon: Network,
    title: "Network-agnostic by design",
    body: "We're not tied to one payment network. Our architecture plugs into multiple global players, so as the market evolves, your integration doesn't have to.",
  },
  {
    icon: Lock,
    title: "Message-grade security",
    body: "Every instruction and status update is encrypted, authenticated, and auditable — engineered to the standards regulators and networks expect.",
  },
  {
    icon: Zap,
    title: "Built for speed to market",
    body: "Skip months of individual integrations, certifications and sandbox cycles. One integration with Pay10x reaches the corridors your business needs.",
  },
  {
    icon: MapPin,
    title: "Singapore roots, global reach",
    body: "Based in one of the world's leading fintech and payments hubs. Built on deep regional expertise and structured for international scale.",
  },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
    <section
  className="nav-dark relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
  style={{ background: "var(--p10-ink)" }}
>
        <div className="grain" />
        {/* faint grid backdrop */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }} />

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Eyebrow dark>Cross-border payments · Singapore</Eyebrow>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.2,0.7,0.2,1] }}
                className="font-serif-display text-white text-[46px] sm:text-6xl lg:text-[88px] leading-[0.95] mt-6"
              >
                One connection. <br />
                <span className="italic text-[color:var(--p10-warm)]">Every corridor.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="mt-8 text-lg lg:text-xl text-white/70 max-w-2xl leading-relaxed"
              >
                Pay10x is the integration layer that turns global payment networks into a
                single, simple connection for your business. Send, receive, and reconcile
                payments anywhere — without building or maintaining the connectivity yourself.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link data-testid="hero-cta-talk" to="/contact" className="btn-square btn-electric group">
                  Talk to our team
                  <ArrowUpRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link data-testid="hero-cta-api" to="/developers" className="btn-square btn-ghost-dark">
                  Explore the API
                </Link>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="mt-10 text-sm text-white/45 max-w-lg leading-relaxed"
              >
                Singapore-based. Globally connected. Built for banks, MTOs, PSPs, fintechs
                and platforms that move money across borders.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <TransactionDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[color:var(--p10-warm)]/40 border-y border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {TRUST.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.08}>
                <div className="flex items-start gap-4">
                  <t.icon className="w-5 h-5 mt-1 text-[color:var(--p10-royal)] shrink-0" />
                  <div>
                    <div className="text-[color:var(--p10-ink)] font-medium text-sm">{t.label}</div>
                    <div className="text-[color:var(--p10-ink)]/60 text-xs mt-1">{t.sub}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow>What we do</Eyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
                Cross-border payments are only as good as the <em className="text-[color:var(--p10-royal)]">plumbing</em> behind them.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <Reveal>
                <p className="text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                  Every network you connect to means new APIs, new formats, new certification
                  cycles, and new operational overhead. Pay10x removes that burden. We
                  integrate once with the world's major payment networks and expose a single,
                  unified endpoint to you.
                </p>
                <p className="mt-6 text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                  Your team builds against one clean API. We handle the translation, routing,
                  monitoring and reconciliation underneath — across whichever combination of
                  global networks best serves your corridors, currencies and use case.
                </p>
                <p className="mt-6 font-serif-display italic text-2xl text-[color:var(--p10-royal)]">
                  You focus on your customers. We focus on the connections.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
  className="nav-dark py-24 md:py-32 relative"
  style={{ background: "var(--p10-ink)" }}
>
        <div className="grain" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <SectionHeader
            dark
            eyebrow="How it works"
            title={<>Three steps. <span className="italic text-[color:var(--p10-warm)]">Zero plumbing.</span></>}
            intro="Integrate once, and Pay10x handles translation, routing, monitoring and reconciliation across every network underneath."
          />

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", title: "Connect once", body: "Integrate with Pay10x's single API and get access to a growing set of global payment networks — without separate onboarding, certification, or technical builds for each one." },
              { n: "02", title: "We route intelligently", body: "Your payment instructions are translated, validated, and routed to the right network partner for the corridor, currency, or method you need — with full message integrity end to end." },
              { n: "03", title: "Track everything, live", body: "Live status updates, settlement confirmations, and reconciliation data come back through the same connection — normalized into one consistent format regardless of network." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: i * 0.15 }}
                className="p-10 md:p-12 bg-[color:var(--p10-ink)]"
              >
                <div className="font-serif-display text-[color:var(--p10-electric)] text-6xl md:text-7xl leading-none">
                  {s.n}
                </div>
                <div className="mt-8 font-serif-display text-white text-3xl leading-tight">
                  {s.title}
                </div>
                <p className="mt-4 text-white/60 leading-relaxed text-[15px]">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PAY10X */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-7">
              <Eyebrow>Why Pay10x</Eyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
                Built for the businesses that move the <em className="text-[color:var(--p10-royal)]">world's money</em>.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-[color:var(--p10-ink)]/70 leading-relaxed">
                Add reach, resilience and speed to market without re-engineering your systems
                every time a new corridor opens up.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[color:var(--p10-line)] border border-[color:var(--p10-line)]">
            {WHY.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-[color:var(--p10-ivory)] p-10 md:p-14 group hover:bg-white transition-colors"
              >
                <w.icon className="w-7 h-7 text-[color:var(--p10-royal)]" />
                <h3 className="font-serif-display text-3xl mt-8 leading-tight">
                  {w.title}
                </h3>
                <p className="mt-4 text-[color:var(--p10-ink)]/70 leading-relaxed">
                  {w.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS PREVIEW */}
      <section className="py-24 md:py-32 bg-white border-y border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>Solutions</Eyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl mt-6 leading-[1.02]">
                Everything you need to move money — <em className="text-[color:var(--p10-royal)]">without the plumbing</em>.
              </h2>
              <Link data-testid="home-solutions-link" to="/solutions" className="btn-square btn-primary mt-10 group inline-flex">
                Explore all solutions
                <ArrowUpRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <ul>
                {[
                  { icon: Network, t: "Payment Connectivity & Orchestration" },
                  { icon: Radar,   t: "Real-Time Status & Settlement Visibility" },
                  { icon: Lock,    t: "Secure Message Infrastructure" },
                  { icon: Layers,  t: "Multi-Network Reach" },
                  { icon: Terminal,t: "Developer-First Integration" },
                ].map((s, i) => (
                  <Reveal key={s.t} delay={i * 0.08}>
                    <li className="group flex items-center justify-between py-6 border-b border-[color:var(--p10-line)] hover:pl-2 transition-all">
                      <div className="flex items-center gap-5">
                        <s.icon className="w-5 h-5 text-[color:var(--p10-royal)]" />
                        <span className="text-xl md:text-2xl font-serif-display">{s.t}</span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[color:var(--p10-ink)]/30 group-hover:text-[color:var(--p10-royal)] transition-colors" />
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[color:var(--p10-ivory)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <Eyebrow>Ready when you are</Eyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
                Skip the certifications. <br />
                <em className="text-[color:var(--p10-royal)]">Ship the corridor.</em>
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
              <Link data-testid="home-cta-contact" to="/contact" className="btn-square btn-primary">Get in touch</Link>
              <Link data-testid="home-cta-demo" to="/contact" className="btn-square btn-secondary">Book a demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}